// Keep in sync with the fade animation in the response card styles.
const FRESH_TEXT_MS = 1200;

export interface FreshTextMark {
  offset: number;
  at: number;
}

/**
 * Escapes the text and applies `**bold**` and line breaks. New pieces get a `.fresh-text` span.
 * The negative animation delay is the age of the piece, so a re-render does not restart the fade.
 */
export function formatAnswerHtml(text: string, fresh: FreshTextMark[] = [], now = Date.now()): string {
  const value = text ?? '';
  const marks = fresh
    .filter(m => m.offset < value.length && now - m.at < FRESH_TEXT_MS)
    .sort((a, b) => a.offset - b.offset);
  const starts = [0, ...marks.map(m => m.offset)];

  return starts.map((start, i) => {
    const html = formatPlainText(value.slice(start, starts[i + 1] ?? value.length));
    if (i === 0) return html;
    return `<span class="fresh-text" style="animation-delay: -${now - marks[i - 1].at}ms">${html}</span>`;
  }).join('');
}

function formatPlainText(text: string): string {
  return escapeHtml(text)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>');
}

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

const MD_FENCE = /^\s*```/;
const MD_ROW = /^\s*\|.*\|\s*$/;
const MD_ITEM = /^\s*(?:[-*]|(\d+)\.)\s+(.*)$/;
const MD_HEADING = /^\s*#{1,6}\s+(.*)$/;
const MD_RULE = /^\s*(?:-{3,}|\*{3,})\s*$/;

/** The markdown MAUI writes: tables, lists, headings, code, rules and bold. Everything is escaped first. */
export function formatMarkdownHtml(text: string): string {
  const lines = (text ?? '').split('\n');
  const html: string[] = [];
  const inline = (s: string) => escapeHtml(s)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  const cells = (row: string, tag: string) =>
    `<tr>${row.trim().replace(/^\||\|$/g, '').split('|').map(c => `<${tag}>${inline(c.trim())}</${tag}>`).join('')}</tr>`;
  const special = (l: string) => [MD_FENCE, MD_ROW, MD_ITEM, MD_HEADING, MD_RULE].some(r => r.test(l));
  let i = 0;
  const take = (test: (l: string) => boolean) => {
    const block: string[] = [];
    while (i < lines.length && test(lines[i])) block.push(lines[i++]);
    return block;
  };

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
    } else if (MD_FENCE.test(line)) {
      i++;
      const code = take(l => !MD_FENCE.test(l));
      i++;
      html.push(`<pre><code>${escapeHtml(code.join('\n'))}</code></pre>`);
    } else if (MD_ROW.test(line)) {
      const [head, ...body] = take(l => MD_ROW.test(l)).filter(r => !/^[\s|:-]+$/.test(r));
      html.push(`<table><thead>${cells(head, 'th')}</thead><tbody>${body.map(r => cells(r, 'td')).join('')}</tbody></table>`);
    } else if (MD_ITEM.test(line)) {
      const items = take(l => MD_ITEM.test(l)).map(l => l.match(MD_ITEM)!);
      const start = items[0][1];
      const open = start ? (start === '1' ? '<ol>' : `<ol start="${start}">`) : '<ul>';
      html.push(`${open}${items.map(m => `<li>${inline(m[2])}</li>`).join('')}${start ? '</ol>' : '</ul>'}`);
    } else if (MD_HEADING.test(line)) {
      html.push(`<h4>${inline(line.match(MD_HEADING)![1])}</h4>`);
      i++;
    } else if (MD_RULE.test(line)) {
      html.push('<hr>');
      i++;
    } else {
      html.push(`<p>${take(l => Boolean(l.trim()) && !special(l)).map(inline).join('<br>')}</p>`);
    }
  }
  return html.join('');
}

const MD_LINK = /!?\[([^\]]*)\]\(\s*<?([^)\s>]*)>?[^)]*\)/g;
const WEB_LINK = /^(https?:|mailto:)/i;
// Web pages (.html) are ordinary links, not attachments
const FILE_EXT = /\.(pdf|docx?|xlsx?|csv|pptx?|zip|png|jpe?g|gif|svg|md|txt|json)([?#].*)?$/i;
const LIST_MARK = /^\s*(?:[-*]|\d+\.)\s*/;

/**
 * Removes links and images that point to files the agent created (reports, exports). They live
 * in the agent's workspace, so the chat cannot show them. Lines left without text are dropped,
 * and so are labels like "Download:" or "Files:" that only introduced such files.
 */
export function stripAttachments(text: string): string {
  const lines = (text ?? '').split('\n');
  const kept = lines.map(line => {
    const stripped = line.replace(MD_LINK, (link, _label, target: string) => isAttachment(target) ? '' : link);
    if (stripped === line) return line;
    const rest = stripped.replace(LIST_MARK, '').trim();
    return /[\p{L}\p{N}]/u.test(rest) && !rest.endsWith(':') ? stripped.replace(/(\S) {2,}/g, '$1 ').trimEnd() : null;
  });
  if (!kept.some((line, i) => line !== lines[i])) return text ?? '';

  return kept.filter((line, i) => {
    if (line === null) return false;
    if (!line.trim().endsWith(':')) return true;
    // The list under a label may start after a blank line
    const start = lines.findIndex((l, j) => j > i && Boolean(l.trim()));
    const end = start < 0 ? -1 : lines.findIndex((l, j) => j > start && !l.trim());
    const block = start < 0 ? [] : kept.slice(start, end < 0 ? undefined : end);
    return !block.length || block.some(l => l !== null);
  }).join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

function isAttachment(target: string): boolean {
  if (target.startsWith('#')) return false;
  return !WEB_LINK.test(target) || FILE_EXT.test(target);
}
